<template>
  <div>
    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center mb-1 mb-md-0">
            <feather-icon icon="HeadphonesIcon" size="24" class="mr-1" />
            <h4 class="mb-0">Chat de Suporte</h4>
          </b-col>
        </b-row>

        <b-row class="mt-1">
          <b-col cols="12" md="4" class="mb-1">
            <b-form-select v-model="statusFilter" :options="statusOptions" @change="fetchConversations" />
          </b-col>
          <b-col cols="12" md="4" class="mb-1">
            <b-form-input v-model="searchQuery" placeholder="Buscar por cliente, assunto..." @input="onSearch" />
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="chatTable"
        :items="conversations"
        :fields="tableColumns"
        responsive
        show-empty
        empty-text="Nenhuma conversa encontrada"
        :busy="loading"
        hover
        class="position-relative"
        @row-clicked="openDetail"
      >
        <template #cell(id)="data">
          <span class="font-weight-bold">#{{ data.value }}</span>
        </template>

        <template #cell(cliente)="data">
          <div>
            <span class="font-weight-bold d-block">{{ data.item.nome_cliente || '-' }}</span>
            <small class="text-muted">{{ data.item.email_cliente || '' }}</small>
          </div>
        </template>

        <template #cell(tipo)="data">
          <b-badge pill :variant="data.value === 'vendedor' ? 'info' : 'secondary'">
            {{ data.value === 'vendedor' ? 'Vendedor' : 'Suporte' }}
          </b-badge>
        </template>

        <template #cell(status)="data">
          <b-badge pill :variant="statusVariant(data.value)">
            {{ statusLabel(data.value) }}
          </b-badge>
        </template>

        <template #cell(data_inclusao)="data">
          <span class="text-nowrap">{{ formatDate(data.value) }}</span>
        </template>

        <template #cell(acoes)="data">
          <b-button
            size="sm"
            variant="flat-primary"
            @click.stop="openDetail(data.item)"
          >
            <feather-icon icon="EyeIcon" class="mr-50" />
            Ver
          </b-button>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" class="d-flex align-items-center justify-content-center">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1"
              prev-class="prev-item"
              next-class="next-item"
              @change="onPageChange"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BBadge, BButton, BFormInput, BFormSelect, BPagination,
} from 'bootstrap-vue'
import { useApi } from '@/composables/useApi'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BBadge,
    BButton,
    BFormInput,
    BFormSelect,
    BPagination,
  },
  data() {
    return {
      conversations: [],
      loading: false,
      statusFilter: '',
      searchQuery: '',
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      pollingInterval: null,
      searchTimeout: null,
      tableColumns: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'tipo', label: 'Tipo' },
        { key: 'cliente', label: 'Cliente' },
        { key: 'assunto', label: 'Assunto' },
        { key: 'categoria', label: 'Categoria' },
        { key: 'status', label: 'Status' },
        { key: 'data_inclusao', label: 'Data', sortable: true },
        { key: 'acoes', label: 'Ações' },
      ],
      statusOptions: [
        { value: '', text: 'Todos' },
        { value: 'aguardando', text: 'Aguardando' },
        { value: 'em_atendimento', text: 'Em Atendimento' },
        { value: 'resolvido', text: 'Resolvido' },
        { value: 'fechado', text: 'Fechado' },
      ],
    }
  },
  mounted() {
    this.fetchConversations()
    this.pollingInterval = setInterval(() => {
      this.fetchConversations(true)
    }, 10000)
  },
  beforeDestroy() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
  methods: {
    async fetchConversations(silent = false) {
      if (!silent) this.loading = true
      try {
        const api = useApi()
        const params = {
          page: this.currentPage,
          perPage: this.perPage,
        }
        if (this.statusFilter) params.status = this.statusFilter
        if (this.searchQuery) params.q = this.searchQuery
        const { data } = await api.get('/chat', { params })
        this.conversations = data.rows || data.data || data || []
        this.totalRows = data.count || data.total || this.conversations.length
      } catch (err) {
        console.error('Erro ao buscar conversas:', err)
      } finally {
        this.loading = false
      }
    },
    onSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.fetchConversations()
      }, 500)
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchConversations()
    },
    openDetail(item) {
      this.$router.push({ name: 'app-chat-suporte-detail', params: { id: item.id } })
    },
    statusVariant(status) {
      const map = {
        aguardando: 'warning',
        em_atendimento: 'info',
        resolvido: 'success',
        fechado: 'secondary',
      }
      return map[status] || 'primary'
    },
    statusLabel(status) {
      const map = {
        aguardando: 'Aguardando',
        em_atendimento: 'Em Atendimento',
        resolvido: 'Resolvido',
        fechado: 'Fechado',
      }
      return map[status] || status
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    },
  },
}
</script>
